import { IS_PWA } from 'constants/platform'
import React, { forwardRef, useRef } from 'react'
import ReactPlayer from 'react-player'

import { UploadButton } from 'partials/UploadButton'
import { AddStepButton } from 'partials/WorkoutEditorForm/subcomponents/WorkoutStepsForm/AddStepButton'

import { Box } from 'widgets/Box'
import { Separator } from 'widgets/Separator'

import { useMaxWidth } from 'hooks/useMaxWidth'

import { useTextEditorModal } from 'contexts/TextEditorModalContext'

import { AllowedFileType, NativeFile } from 'types/file'

import { WorkoutStepRow } from '../WorkoutStepsForm/WorkoutStepRow'

interface Props<Values> {
  initialValues: Values
  onValuesChange: (values: Partial<Values>) => void
  allowedFileType: AllowedFileType
  setFiles: ((file: NativeFile) => void) | ((file: React.ChangeEvent<HTMLInputElement>) => void)
  setFile: ((file: NativeFile) => void) | ((file: React.ChangeEvent<HTMLInputElement>) => void)
  setFileIndex: (index: number) => void
  deleteStepAtIndex: (stepIndex: number) => void
  insertStepAtIndex: (stepIndex: number) => void
  isAudioEnabledSwitches: boolean[]
  toggleIsAudioEnabled: (index: number) => void
}

export const MultipleMediaSelector = forwardRef(
  (
    {
      initialValues,
      onValuesChange,
      allowedFileType,
      setFiles,
      setFile,
      setFileIndex,
      deleteStepAtIndex,
      insertStepAtIndex,
      isAudioEnabledSwitches,
      toggleIsAudioEnabled,
    }: Props,
    ref: any,
  ) => {
    const { width: maxWidth } = useMaxWidth()
    const textEditorModalRef = useTextEditorModal()
    const openImagePickerRef = useRef<any>(null)

    let editingMediaIndex: number | undefined

    const presentTextEditor = (index: number) => {
      textEditorModalRef?.current?.present({
        title: `Step ${index + 1}`,
        inputText: initialValues?.steps[index]?.description,
        onSubmit: (inputText: string) => {
          const { steps } = initialValues
          steps[index].description = inputText?.trim() ?? ''
          onValuesChange({
            ...initialValues,
            steps,
          })
        },
      })
    }

    return (
      <Box width={maxWidth}>
        <Box position='absolute'>
          <UploadButton
            ref={ref}
            multiple
            showSelection={false}
            fileType={allowedFileType}
            onFileSelect={setFiles}
          />
          <UploadButton
            ref={openImagePickerRef}
            fileType={allowedFileType}
            onFileSelect={(file: any) => {
              setFileIndex(editingMediaIndex)
              setFile(file)
            }}
          />
        </Box>

        <Box width='100%'>
          {/* This hack for PWAs allows us to load in uploads with working previews directly.
           * Without the attempt to load a player beforehand, the previews did not work until tapped in PWA mode.
           */}
          {IS_PWA && (
            <ReactPlayer
              url='https://res.cloudinary.com/upswingfitness-prod/video/upload/v1657911402/xlcsowbsrk13jimz4aru.mp4'
              width={0}
              height={0}
            />
          )}

          {!initialValues.steps?.length ? (
            <Box margin={{ top: 'xl', bottom: 'sm', horizontal: 'lg' }} padding={{ top: 'md' }}>
              <AddStepButton onPress={() => insertStepAtIndex(0)} />
            </Box>
          ) : (
            <Box margin='lg' padding={{ horizontal: 'md' }}>
              <Separator />
            </Box>
          )}

          {initialValues?.steps?.map((step: any, index: number) => {
            return (
              <WorkoutStepRow
                file={step?.file}
                title={`Step ${index + 1}`}
                description={initialValues?.steps[index]?.description}
                onDescriptionEdit={() => presentTextEditor(index)}
                onMediaEdit={() => {
                  editingMediaIndex = index
                  openImagePickerRef?.current?.openFileUpload()
                }}
                onDelete={() => deleteStepAtIndex(index)}
                onInsert={() => insertStepAtIndex(index + 1)}
                isMuted={!isAudioEnabledSwitches[index]}
                onMuteToggle={() => toggleIsAudioEnabled(index)}
              />
            )
          })}
        </Box>
      </Box>
    )
  },
)
